import React from "react";
import { Box, Button, Divider, Grid, Paper, Typography } from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import CustomTextInput from "../CustomTextInput";
import CountrySelect from "./CountrySelect";
import OMNSubdivisionSelect from "./OMNSubdivisionSelect";
import RegistrationTypeSelect from "./RegistrationTypeSelect";
import { showSnackbar } from "../../api/snackbarSlice";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, some } from "lodash";
import { OMN_COUNTRY_CODE } from "../../constants/defaults";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomerValidation } from "../../validations/CustomerValidation";
import {
  getCountryOptionByCode,
  getRegistrationTypeOptionByCode,
  getSubdivisionOptionByCode,
} from "../../api/codelist/codeListsSlice";

export default function CustomerPopupForm({ initialData, onClose, onSave }) {
  const dispatch = useDispatch();
  const countryOption = useSelector(
    getCountryOptionByCode(initialData.postal_address?.country_code),
  );
  const subdivisionOption = useSelector(
    getSubdivisionOptionByCode(initialData.postal_address?.country_subentity),
  );
  const schemeAgencyIdOption = useSelector(
    getRegistrationTypeOptionByCode(initialData.scheme_agency_id),
  );
  const passportIssuingCountryOption = useSelector(
    getCountryOptionByCode(initialData.passport_issuing_country_code),
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(CustomerValidation(true)),
    defaultValues: {
      name: initialData.name || "",
      email: initialData.email || "",
      postal_address: {
        street_name: initialData.postal_address?.street_name || "",
        city_name: initialData.postal_address?.city_name || "",
        postal_zone: initialData.postal_address?.postal_zone || "",
        additional_street_name:
          initialData.postal_address?.additional_street_name || "",
        address_line: initialData.postal_address?.address_line || "",
        country_subentity:
          initialData.postal_address?.country_code === OMN_COUNTRY_CODE
            ? (subdivisionOption ?? null)
            : initialData.postal_address?.country_subentity || "",
        country_code: countryOption || "",
      },
      passport_issuing_country_code: passportIssuingCountryOption || "",
      scheme_agency_id: schemeAgencyIdOption || "",
      company_id: initialData.company_id || "",
    },
  });

  const hasAtLeastOneFieldFilled = (data) => {
    return some(data, (value) => {
      if (typeof value === "object" && value !== null) {
        return !isEmpty(value?.key) || !isEmpty(value?.value);
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  const handleCountryChange = (value) => {
    setValue(
      "postal_address.country_subentity",
      value?.code === OMN_COUNTRY_CODE ? null : "",
    );
  };

  const preparePayload = (data) => {
    return {
      ...data,
      scheme_agency_id: data.scheme_agency_id?.code,
      tin: data?.tin?.trim() || data?.tin || "",
      postal_address: {
        ...data.postal_address,
        country_code: data.postal_address?.country_code?.code,
        country: data.postal_address?.country_code?.label,
        country_subentity:
          data.postal_address?.country_code?.code === OMN_COUNTRY_CODE
            ? data.postal_address?.country_subentity?.code
            : data.postal_address?.country_subentity,
      },
      passport_issuing_country_code: data.passport_issuing_country_code?.code,
    };
  };

  const handleProceed = (customerData) => {
    if (!hasAtLeastOneFieldFilled(customerData)) {
      dispatch(
        showSnackbar({
          message: "Please fill atleast one field to proceed",
          severity: "error",
        }),
      );
    } else {
      const customer = preparePayload(customerData);
      onSave({ ...customer });
      onClose();
    }
  };

  const registrationType = useWatch({ control, name: "scheme_agency_id" });
  const countryCode = useWatch({
    control,
    name: "postal_address.country_code",
  })?.code;

  const registrationNumberLabel = registrationType
    ? registrationType.code
      ? `${registrationType.label} (${registrationType.code})`
      : registrationType.label
    : "Registration Number";
  return (
    <Paper elevation={0} sx={{ p: "1rem", overflow: "auto" }}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              name="name"
              control={control}
              errors={errors}
              label="Full Name"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              name="email"
              control={control}
              errors={errors}
              label="Email"
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
          Address
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="street_name-input"
              name="postal_address.street_name"
              control={control}
              errors={errors}
              label={"Street Name"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="additional_street_name-input"
              name="postal_address.additional_street_name"
              control={control}
              errors={errors}
              label={"Additional Street Name"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="city_name-input"
              name="postal_address.city_name"
              control={control}
              errors={errors}
              label={"City Name"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              name="postal_address.address_line"
              control={control}
              errors={errors}
              label={"Address Line"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountrySelect
              data-testid="postal_address.country_code-select"
              name="postal_address.country_code"
              control={control}
              errors={errors}
              label={"Country"}
              onChange={handleCountryChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {countryCode === OMN_COUNTRY_CODE ? (
              <OMNSubdivisionSelect
                data-testid="country-subentity-select"
                name="postal_address.country_subentity"
                control={control}
                errors={errors}
                label={"Country Subentity"}
              />
            ) : (
              <CustomTextInput
                data-testid="country-subentity-input"
                name="postal_address.country_subentity"
                control={control}
                errors={errors}
                label={"Country Subentity"}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="postal_zone-input"
              name="postal_address.postal_zone"
              control={control}
              errors={errors}
              label={"Postal Zone"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-tetsid="telephone-input"
              name="telephone"
              control={control}
              errors={errors}
              label={"Telephone"}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
          Business Identifiers
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RegistrationTypeSelect
              data-testid="registration-type-select"
              errors={errors}
              control={control}
              name="scheme_agency_id"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="registration-number-input"
              name="company_id"
              control={control}
              errors={errors}
              label={registrationNumberLabel}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountrySelect
              data-testid="passport_issuing_country_code-select"
              name="passport_issuing_country_code"
              control={control}
              errors={errors}
              label={"Passport Issuing Country"}
              disabled={registrationType?.code !== "PASNUM"}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button variant="contained" onClick={handleSubmit(handleProceed)}>
          Save
        </Button>
      </Box>
    </Paper>
  );
}
